import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Title,
  Anchor,
} from "@mantine/core";

import {
  ADMIN_ORGANIZATION,
  ADMIN_ROLE,
  HTML_PAGE_CREATE_AUTHORISED_ORGANIZATIONS,
  HTML_PAGE_CREATE_AUTHORISED_ROLES,
} from "_roles";

import type { HtmlPage } from "htmlpage/types/html-page.model";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOrganization } from "common/admin/auth/hooks";
import QueryWrapper from "common/components/QueryWrapper";
import { Th } from "common/components/table/Th";

import {
  useListHTMLPagesQuery,
  useRemoveHTMLPageMutation,
} from "htmlpage/api/html-page.api";

import { HtmlPageAdminRow } from "../components/HTMLPageAdminRow";
import { ADMIN_PATH } from "_urls";

type SortedKeys = keyof Pick<
  HtmlPage,
  "creationTime" | "name" | "organization"
>;

function sortData(
  data: HtmlPage[],
  payload: { sortBy: SortedKeys; reversed: boolean },
): HtmlPage[] {
  return [...data].sort((a, b) => {
    if (payload.reversed) {
      return String(b[payload.sortBy]).localeCompare(String(a[payload.sortBy]));
    }
    return String(a[payload.sortBy]).localeCompare(String(b[payload.sortBy]));
  });
}

export function HtmlPagesListAdminView() {
  const navigate = useNavigate();
  const listHTMLPagesQuery = useListHTMLPagesQuery();
  const [sortedData, setSortedData] = useState<HtmlPage[]>([]);
  const [sortBy, setSortBy] = useState<SortedKeys>("creationTime");
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [organization] = useOrganization();

  const htmlPages = useMemo(
    () => listHTMLPagesQuery.data,
    [listHTMLPagesQuery.data],
  );

  const filteredHtmlPages = useMemo(
    () =>
      (htmlPages || []).filter(
        (htmlPage) => htmlPage.creationTime && htmlPage.name,
      ),
    [htmlPages],
  );

  const [deleteHTMLPage] = useRemoveHTMLPageMutation();

  const handleDelete = async (id: string) => {
    try {
      await deleteHTMLPage(id).unwrap();
      console.log(`HTML page ${id} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete HTML page", error);
    }
  };

  const triggerSortingData = useCallback(
    (field: SortedKeys) => {
      const reversed = field === sortBy ? !reverseSortDirection : false;
      return sortData(filteredHtmlPages, { sortBy: field, reversed });
    },
    [filteredHtmlPages, reverseSortDirection, sortBy],
  );

  useEffect(() => {
    setSortedData(triggerSortingData(sortBy));
  }, [filteredHtmlPages, sortBy, triggerSortingData]);

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align="center" m="xs">
          HTML Pages
        </Title>
        <Breadcrumbs separator="→">
          <Anchor component={Link} to={`/${ADMIN_PATH}`}>
            Admin
          </Anchor>

          <span>HTML Pages</span>
        </Breadcrumbs>

        <AccessRights
          authorizedOrganizations={HTML_PAGE_CREATE_AUTHORISED_ORGANIZATIONS}
          authorizedRoles={HTML_PAGE_CREATE_AUTHORISED_ROLES}
        >
          <Group position="right">
            <Button className="bg-button-admin" component={Link} to="new">
              New
            </Button>
          </Group>
        </AccessRights>
      </div>
      <Card shadow={"sm"} p={"xl"} className={"overflow-visible"}>
        {/* collapasible changed to card */}
        <QueryWrapper query={listHTMLPagesQuery}>
          {filteredHtmlPages && filteredHtmlPages.length > 0 ? (
            <Table highlightOnHover>
              <thead>
                <tr>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "name"}
                    onClick={() => {
                      if (sortBy === "name") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("name");
                      }
                    }}
                  >
                    Name
                  </Th>
                  <AccessRights
                    authorizedOrganizations={[ADMIN_ORGANIZATION]}
                    authorizedRoles={[ADMIN_ROLE]}
                  >
                    <Th
                      reversed={reverseSortDirection}
                      sorted={sortBy === "organization"}
                      onClick={() => {
                        if (sortBy === "organization") {
                          setReverseSortDirection(!reverseSortDirection);
                        } else {
                          setSortBy("organization");
                        }
                      }}
                    >
                      Organization
                    </Th>
                  </AccessRights>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "creationTime"}
                    onClick={() => {
                      if (sortBy === "creationTime") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("creationTime");
                      }
                    }}
                  >
                    Creation Date
                  </Th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((page) =>
                  page.organization === organization ||
                  organization === ADMIN_ORGANIZATION ? (
                    <HtmlPageAdminRow
                      key={page.id}
                      htmlPage={page}
                      onEdit={() => navigate(page.id)}
                      onDelete={() => handleDelete(page.id)}
                      onDuplicate={() => navigate(`${page.id}/duplicate`)}
                    />
                  ) : null,
                )}
              </tbody>
            </Table>
          ) : (
            <Stack align="center">
              <Box>New HTML Page</Box>
              <Button className="bg-button-admin" component={Link} to="new">
                New
              </Button>
            </Stack>
          )}
        </QueryWrapper>
      </Card>
    </Box>
  );
}
